import { Badge } from "./badge";

/**
 * Badges de statut, un composant par famille de statut.
 *
 * Volontairement PAS un composant unique prenant `statut: string` : chaque
 * famille a son propre jeu de valeurs et son propre mapping vers un variant,
 * et un `string` commun ferait perdre le typage aux appelants qui l'ont déjà.
 *
 * Les unions sont recopiées ici plutôt qu'importées des enums Convex : ce
 * package est du rendu pur et ne doit pas dépendre du backend.
 */

export type StatutParcours = "en_cours" | "termine" | "archive";

/** Statut d'un parcours candidat. `undefined` = non commencé. */
export function StatutParcoursBadge({
  statut,
}: {
  // `string & {}` garde l'autocomplétion des valeurs connues tout en
  // acceptant un statut brut venant de la DB.
  statut: StatutParcours | (string & {}) | undefined;
}) {
  switch (statut) {
    case "en_cours":
      // `warning` et non `default` : « en cours » est un état intermédiaire,
      // pas une action primaire. Aligne les trois implémentations qui
      // divergeaient (plan §0A.3).
      return <Badge variant="warning">En cours</Badge>;
    case "termine":
      return <Badge variant="success">Terminé</Badge>;
    case "archive":
      return <Badge variant="secondary">Archivé</Badge>;
    default:
      return <Badge variant="outline">Non commencé</Badge>;
  }
}

export type StatutCampagne = "brouillon" | "active" | "terminee";

/** Statut d'une campagne. Une valeur inconnue est affichée telle quelle. */
export function StatutCampagneBadge({
  statut,
}: {
  statut: StatutCampagne | (string & {});
}) {
  switch (statut) {
    case "active":
      return <Badge variant="success">Active</Badge>;
    case "terminee":
      return <Badge variant="secondary">Terminée</Badge>;
    case "brouillon":
      return <Badge variant="outline">Brouillon</Badge>;
    default:
      return <Badge variant="outline">{statut}</Badge>;
  }
}

export type StatutNotation =
  | "non_commencee"
  | "en_cours"
  | "terminee"
  | "erreur"
  | "annulee";

const LABELS_NOTATION: Record<StatutNotation, string> = {
  non_commencee: "Non commencée",
  en_cours: "En cours",
  terminee: "Terminée",
  erreur: "Erreur",
  annulee: "Annulée",
};

/**
 * Statut d'une notation (cycle de vie d'une note par évaluateur).
 *
 * `aFaire` change la perspective sur `non_commencee` : dans la liste de travail
 * de l'évaluateur c'est une action attendue de lui (« À faire », `purple`),
 * alors qu'en suivi côté admin c'est un simple état constaté
 * (« Non commencée », `outline`).
 */
export function StatutNotationBadge({
  statut,
  aFaire = false,
}: {
  statut: StatutNotation;
  aFaire?: boolean;
}) {
  const label = LABELS_NOTATION[statut];
  switch (statut) {
    case "terminee":
      return <Badge variant="success">{label}</Badge>;
    case "erreur":
      return <Badge variant="destructive">{label}</Badge>;
    case "en_cours":
      return <Badge variant="warning">{label}</Badge>;
    case "non_commencee":
      if (aFaire) return <Badge variant="purple">À faire</Badge>;
      return <Badge variant="outline">{label}</Badge>;
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
}

export type StatutEvaluation =
  | "non_commence"
  | "en_cours"
  | "soumis"
  | "analyse_en_cours"
  | "termine"
  | "erreur_analyse";

/**
 * Statut d'une évaluation dans un parcours (`StatutGlobalEnum` côté Convex).
 *
 * `detailAnalyse` gouverne la visibilité de la phase d'analyse LLM :
 * - `true` (admin, client) : `analyse_en_cours` et `erreur_analyse` ont leur
 *   propre badge, testés AVANT « soumise » car ils en font partie.
 * - `false` (candidat, défaut) : ces deux statuts s'affichent « Terminée ». Le
 *   candidat a soumis, la suite du traitement ne le concerne pas.
 *
 * La divergence était auparavant implicite entre trois copies de
 * `getStatusBadge` ; elle est ici un prop explicite.
 */
export function StatutEvaluationBadge({
  statut,
  detailAnalyse = false,
}: {
  statut: StatutEvaluation | (string & {});
  detailAnalyse?: boolean;
}) {
  if (detailAnalyse) {
    if (statut === "analyse_en_cours")
      return <Badge variant="warning">Analyse en cours</Badge>;
    if (statut === "erreur_analyse")
      return <Badge variant="destructive">Erreur d'analyse</Badge>;
  }
  // Miroir de `estEvaluationSoumise` (convex/schema.ts) : tout ce qui n'est ni
  // « non commencé » ni « en cours » est soumis. Réimplémenté et non importé —
  // ce package est du rendu pur, sans dépendance au backend.
  if (statut !== "non_commence" && statut !== "en_cours")
    return <Badge variant="success">Terminée</Badge>;
  if (statut === "en_cours") return <Badge variant="warning">En cours</Badge>;
  return <Badge variant="purple">À faire</Badge>;
}
